import { type Key, useCallback, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router';
import { updateURLSearchParamsWithProjectContext } from '@gonfalon/app-context/updateURLSearchParamsWithProjectContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { enableCachedLoader, enableEnvironmentSwitcherUrlProjectContext } from '@gonfalon/dogfood-flags/dogfood-flags';
import { putSelectedEnvironments } from '@gonfalon/openapi/operations/putSelectedEnvironments';
import { internalEnvironmentList } from '@gonfalon/rest-api/queries/environments-options';
import { selectedEnvironments } from '@gonfalon/rest-api/queries/selectedEnvironments';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import { type SelectedEnvironment } from '../types';

type MutationSnapshot = {
  committedClock: string;
  environments: SelectedEnvironment[];
  selectedEnvironmentKey: string | undefined;
};

// Shared across every `useEnvironmentController` for a project (toolbar + flag list
// columns) so in-flight add/remove from one surface is visible to the other.
const mutationSnapshotQueryKey = (projectKey: string) =>
  ['chrome', 'environment-controller', 'mutation-snapshot', projectKey] as const;

export const useEnvironmentController = ({
  projectKey,
  filterSearchParams,
}: {
  projectKey: string;
  filterSearchParams?: ({ params }: { params: URLSearchParams }) => URLSearchParams;
}) => {
  const selectedEnvironmentsQuery = selectedEnvironments({ projectKey });
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: savedProjectContext } = useSuspenseQuery(selectedEnvironmentsQuery);
  const { environments: apiEnvironments, selectedEnvironmentKey: apiSelectedEnvironmentKey } = savedProjectContext;

  const useUrlProjectContext = enableEnvironmentSwitcherUrlProjectContext();
  const projectContext = useProjectContext({ optional: true });
  // A caller may ask about a project other than the one the route loaded. Only trust the loader's
  // snapshot when it describes the same project.
  const hasProjectContext =
    useUrlProjectContext && projectContext !== undefined && projectContext.context.projectKey === projectKey;

  // Display only committed loader data. Pages use that same snapshot, so the selector never moves
  // ahead of the content during navigation.
  const environments = hasProjectContext ? projectContext.environments : apiEnvironments;
  const selectedEnvironmentKey = hasProjectContext
    ? projectContext.context.selectedEnvironmentKey
    : useUrlProjectContext && projectContext !== undefined
      ? apiSelectedEnvironmentKey
      : (searchParams.get('selected-env') ?? apiSelectedEnvironmentKey);

  const queryClient = useQueryClient();

  const committedClock = `${projectKey}\0${environments.map((environment) => environment.key).join('\0')}\0${selectedEnvironmentKey ?? ''}`;
  const snapshotQueryKey = mutationSnapshotQueryKey(projectKey);
  const snapshotQueryHash = queryClient.defaultQueryOptions({ queryKey: snapshotQueryKey }).queryHash;
  const mutationSnapshot = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) =>
        queryClient.getQueryCache().subscribe((event) => {
          if (event.query.queryHash === snapshotQueryHash) {
            onStoreChange();
          }
        }),
      [queryClient, snapshotQueryHash],
    ),
    () => queryClient.getQueryData<MutationSnapshot>(snapshotQueryKey),
    () => queryClient.getQueryData<MutationSnapshot>(snapshotQueryKey),
  );
  const getMutationSnapshot = (): MutationSnapshot => {
    const current = queryClient.getQueryData<MutationSnapshot>(snapshotQueryKey);
    return current?.committedClock === committedClock
      ? current
      : {
          committedClock,
          environments,
          selectedEnvironmentKey,
        };
  };

  const intendedEnvironments = () => (useUrlProjectContext ? getMutationSnapshot().environments : environments);
  const intendedSelectedEnvironmentKey = () =>
    useUrlProjectContext ? getMutationSnapshot().selectedEnvironmentKey : selectedEnvironmentKey;

  // Flag-off matches main: the caller's key is absolute. The page renders whatever the URL
  // names, so re-deriving a selection here would navigate it away from its own content even
  // though the saved list never held that key.
  //
  // Flag-on, the loader's key is optional and the intended list can drop it mid-navigation, so
  // keep it only while the next list still holds it and otherwise fall back to the first
  // environment. An empty list has nothing to select, so the mutation is dropped.
  const commitWithSelection = (nextList: SelectedEnvironment[], preferredKey: string | undefined) => {
    const nextSelectedKey = useUrlProjectContext
      ? preferredKey !== undefined && nextList.some((environment) => environment.key === preferredKey)
        ? preferredKey
        : nextList[0]?.key
      : (preferredKey ?? nextList[0]?.key);

    if (nextSelectedKey === undefined) {
      return;
    }

    commitEnvironments(nextList, nextSelectedKey);
  };

  const addEnvironment = (environment: SelectedEnvironment) => {
    const currentEnvironments = intendedEnvironments();
    if (currentEnvironments.some((current) => current.key === environment.key)) {
      return;
    }

    commitEnvironments([...currentEnvironments, environment], environment.key);
  };
  const removeEnvironment = (key: Key) => {
    const currentEnvironments = intendedEnvironments();
    const currentSelectedEnvironmentKey = intendedSelectedEnvironmentKey();
    const removedIndex = currentEnvironments.findIndex((it) => it.key === key);
    if (removedIndex === -1) {
      return;
    }

    let nextSelectedKey = currentSelectedEnvironmentKey;
    if (currentSelectedEnvironmentKey === key) {
      const previousEnvironment = currentEnvironments.at(Math.abs(removedIndex - 1)) ?? currentEnvironments[0];
      nextSelectedKey = previousEnvironment.key;
    }

    const nextList = currentEnvironments.filter((it) => it.key !== key);

    commitWithSelection(nextList, nextSelectedKey);
  };
  const replaceEnvironment = (sourceEnvironmentKey: Key, targetEnvironment: SelectedEnvironment) => {
    const currentEnvironments = intendedEnvironments();
    const currentSelectedEnvironmentKey = intendedSelectedEnvironmentKey();
    const sourceIndex = currentEnvironments.findIndex((it) => it.key === sourceEnvironmentKey);
    if (sourceIndex === -1) {
      return;
    }

    let nextSelectedKey = currentSelectedEnvironmentKey;
    if (currentEnvironments[sourceIndex].key === currentSelectedEnvironmentKey) {
      nextSelectedKey = targetEnvironment.key;
    }

    const nextList = currentEnvironments.flatMap((environment, index) => {
      if (index === sourceIndex) {
        return [targetEnvironment];
      }
      return environment.key === targetEnvironment.key ? [] : [environment];
    });

    commitWithSelection(nextList, nextSelectedKey);
  };

  // Change selection only. Never accepts a full list from the caller, so committed display
  // cannot overwrite a pending add/remove.
  const selectEnvironment = (key: string) => {
    const currentEnvironments = intendedEnvironments();
    if (!currentEnvironments.some((environment) => environment.key === key)) {
      return;
    }

    commitEnvironments(currentEnvironments, key);
  };

  // Apply a caller-computed order (usually from the displayed list) onto the latest intended
  // list: keys absent from the intended list stay removed, and intended-only keys (pending
  // additions) are kept. Flag-off matches main: take the caller's list as absolute.
  const reorderEnvironments = (orderedEnvironments: SelectedEnvironment[]) => {
    if (!useUrlProjectContext) {
      commitWithSelection(orderedEnvironments, selectedEnvironmentKey);
      return;
    }

    const currentEnvironments = intendedEnvironments();
    const currentSelectedEnvironmentKey = intendedSelectedEnvironmentKey();
    const pendingByKey = new Map(currentEnvironments.map((environment) => [environment.key, environment]));
    const orderedKeys = orderedEnvironments.map((environment) => environment.key);

    const nextList = orderedKeys.flatMap((key) => {
      const environment = pendingByKey.get(String(key));
      return environment ? [environment] : [];
    });

    for (const environment of currentEnvironments) {
      if (!orderedKeys.includes(environment.key)) {
        nextList.push(environment);
      }
    }

    commitWithSelection(nextList, currentSelectedEnvironmentKey);
  };

  const commitEnvironments = (nextEnvironments: SelectedEnvironment[], updatedSelectedEnvironmentKey: string) => {
    const updatedEnvironments = uniqBy(nextEnvironments, 'key');
    const previousEnvironments = intendedEnvironments();
    if (useUrlProjectContext) {
      queryClient.setQueryData<MutationSnapshot>(snapshotQueryKey, {
        committedClock,
        environments: updatedEnvironments,
        selectedEnvironmentKey: updatedSelectedEnvironmentKey,
      });
    }

    let params = searchParams;
    if (filterSearchParams) {
      params = filterSearchParams({ params: searchParams });
    }

    const nextData = {
      projectKey,
      environmentKeys: new Set(updatedEnvironments.map((env) => env.key)),
      selectedEnvironmentKey: updatedSelectedEnvironmentKey,
    };
    const nextParams = updateURLSearchParamsWithProjectContext(params, nextData);

    if (enableCachedLoader()) {
      const oldData = queryClient.getQueryData(
        internalEnvironmentList({
          projectKey: nextData.projectKey,
          query: {
            key: Array.from(new Set(previousEnvironments.map((env) => env.key))).sort(),
          },
        }).queryKey,
      );

      queryClient.setQueryData(
        internalEnvironmentList({
          projectKey: nextData.projectKey,
          query: {
            key: Array.from(nextData.environmentKeys).sort(),
          },
        }).queryKey,
        () => ({
          ...oldData,
          totalCount: nextData.environmentKeys.size,
          items: [...updatedEnvironments],
        }),
      );

      queryClient.setQueryData(selectedEnvironmentsQuery.queryKey, (old) => ({
        ...old,
        environments: updatedEnvironments,
        selectedEnvironmentKey: nextData.selectedEnvironmentKey,
      }));
    }

    const syncMessage = {
      projectKey,
      body: {
        environmentKeys: updatedEnvironments.map((e) => e.key),
        selectedEnvironmentKey: nextData.selectedEnvironmentKey,
      },
    };

    void putSelectedEnvironments(syncMessage);

    // `useOptimisticProjectContext` reads this navigation state and requires a bare
    // InternalEnvironment[] — a wrapper object disables optimistic environment addition.
    //
    // `flushSync` renders the navigation's initial (pending) state synchronously
    // instead of in a React transition. The optimistic env columns paint on the
    // click itself; the default transition is low-priority, gets interrupted and
    // restarted by unrelated urgent updates on busy pages, and is skipped
    // entirely when the loader resolves fast enough for React to batch straight
    // to the settled state.
    setSearchParams(nextParams, { state: updatedEnvironments, flushSync: true });
  };

  const pendingEnvironments =
    useUrlProjectContext && mutationSnapshot?.committedClock === committedClock
      ? mutationSnapshot.environments
      : environments;
  const excludedIds = new Set<string>();
  for (const environment of environments) {
    excludedIds.add(environment._id);
  }
  for (const environment of pendingEnvironments) {
    excludedIds.add(environment._id);
  }

  return {
    addEnvironment,
    removeEnvironment,
    replaceEnvironment,
    selectEnvironment,
    reorderEnvironments,
    environments,
    selectedEnvironmentKey,
    excludedIds,
  };
};
